import { ResourceCompetency } from "../../hooks/useGetResources";

export interface FileItem {
  name: string;
  url: string;
  theme: string;
  imageurl: string;
  competency: string;
  complexity: string;
}

export interface FileFilters {
  competencies?: string[];
  complexity?: string;
  themeSearch?: string;
  themes?: string[];
}

// Returns sorted unique themes for selected competencies.
// If a competency has an active complexity, only that complexity's themes are included.
export function getThemes(
  data: ResourceCompetency[],
  competencies: string[],
  activeComplexity: Record<string, string>
): string[] {
  const competencySet = new Set(competencies);
  const themes = new Set<string>();
  for (const comp of data) {
    if (!competencySet.has(comp.competency)) continue;
    const active = activeComplexity[comp.competency];
    for (const doc of comp.documents) {
      if (active && doc.complexity !== active) continue;
      for (const file of doc.files) themes.add(file.theme);
    }
  }
  return Array.from(themes).sort();
}

// Returns unique competency names in the order they appear in the API response.
export function getCompetencies(data: ResourceCompetency[]): string[] {
  const seen = new Set<string>();
  const competencies: string[] = [];
  for (const comp of data) {
    if (seen.has(comp.competency)) continue;
    seen.add(comp.competency);
    competencies.push(comp.competency);
  }
  return competencies;
}

const norm = (s: string) => s.toLowerCase();

// Returns files matching the given filters.
// Any omitted filter is ignored; no filters at all returns every file.
export function getFiles(data: ResourceCompetency[], filters: FileFilters): FileItem[] {
  const { competencies, complexity, themeSearch, themes } = filters;
  const competencySet = competencies?.length ? new Set(competencies.map(norm)) : null;
  const themeSet = themes?.length ? new Set(themes) : null;
  const search = themeSearch?.trim().toLowerCase() ?? "";
  const result: FileItem[] = [];

  for (const comp of data) {
    if (competencySet && !competencySet.has(norm(comp.competency))) continue;
    for (const doc of comp.documents) {
      if (complexity && norm(doc.complexity) !== norm(complexity)) continue;
      for (const file of doc.files) {
        if (themeSet && !themeSet.has(file.theme)) continue;
        if (search && !file.theme.toLowerCase().includes(search)) continue;
        result.push({ ...file, competency: comp.competency, complexity: doc.complexity });
      }
    }
  }

  return result;
}
